// Task 5 (items-measure hardening): single shared "preview" math module for
// the top SummaryBar panel and every ItemCard header — before this, the same
// volume/weight/dim-weight formulas were duplicated (with small divergences)
// across ItemsMeasureUnits.jsx and ItemCard.jsx. Consolidating them here means
// the two surfaces can never disagree about a live (unsaved) value.
//
// kg/cm normalization is kept byte-identical to the backend resolver: kg -> lb
// is ÷0.453592, cm -> in is ÷2.54. Do not change these constants without
// updating the server resolver too.
import { buildDraft, isEmptyDraft } from "./itemDraftHelpers";

const LB_PER_KG = 0.453592;
const IN_PER_CM = 2.54;
// Same ft³ -> m³ conversion SummaryBar already used (0.0283168) — reused here
// verbatim for density() rather than inventing a new constant.
const M3_PER_FT3 = 0.0283168;

function toInches(value, uom) {
  const num = parseFloat(value || 0);
  if (Number.isNaN(num)) return 0;
  return uom === "cm" ? num / IN_PER_CM : num;
}

// Shared by persisted items and live drafts — `source` only needs to look
// like an item/draft on these four fields, which item/buildDraft's draft both
// use identical field names for (item_length/item_width/item_height/
// item_length_uom/handling_units), so one formula covers both without
// duplicating it.
export function itemVolumeFt3(source) {
  const uom = source.item_length_uom;
  const l = toInches(source.item_length, uom);
  const w = toInches(source.item_width, uom);
  const h = toInches(source.item_height, uom);
  const hus = Math.max(parseInt(source.handling_units, 10) || 0, 0) || 1;
  return (l * w * h * hus) / 1728;
}

// A draft's line weight in lb (weight_total_exact is stored in the draft's
// own uom — the exact total that becomes item_weight on save).
export function itemWeightLb(source) {
  const total = parseFloat(source.weight_total_exact) || 0;
  const uom = String(source.item_weight_uom || "lb").toLowerCase();
  return ["kg", "kgs"].includes(uom) ? total / LB_PER_KG : total;
}

// A draft's dim weight in lb — (L x W x H in inches / dim_factor) x count,
// where `count` follows the org's Dim Weight Calculation Based On config:
// 'quantity' -> item pieces, anything else ('handling_units', the default) ->
// handling units. Mirrors the backend get_dim_values/update_dim_values basis so
// the live preview matches the persisted value. Needs the account/item dim
// factor; with no positive factor, dim weight is NOT derivable — return null
// (not 0) so the caller renders "—" instead of a misleading "0.00".
export function itemDimWeightLb(source, dimFactor, basis) {
  const factor = parseFloat(dimFactor) || 0;
  if (factor <= 0) return null;
  const uom = source.item_length_uom;
  const l = toInches(source.item_length, uom);
  const w = toInches(source.item_width, uom);
  const h = toInches(source.item_height, uom);
  const count =
    basis === "quantity"
      ? Math.max(parseInt(source.item_quantity, 10) || 0, 0) || 1
      : Math.max(parseInt(source.handling_units, 10) || 0, 0) || 1;
  return ((l * w * h) / factor) * count;
}

// B3 (mockup-parity): Quantity (pcs/HUs), Volume, Weight and Dim Weight go
// stale mid-edit — all are pure functions of fields ItemCard's form edits
// directly (item_quantity/handling_units/dims), unlike every other summary
// tile (reweigh sums, chargeable weight, ...), which only reconcile from
// server-side rollups on Save and can't be recomputed from a draft alone.
// While ANY item draft is dirty (or there's a pending "Add N Items" card),
// recompute these display-only totals from the DRAFTS — every persisted
// item's current draft (or its buildDraft(item) baseline when untouched) plus
// every pending new item's draft.
export function computeDraftTotals(items, itemDrafts, pendingNewItems, dimFactor, dimWeightBasis) {
  let pcs = 0;
  let hus = 0;
  let volumeFt3 = 0;
  let weightLb = 0;
  let dimWeightLb = 0;
  // Mirrors computeLiveUnitTotals' `anyBookedDim`: only once a draft actually
  // contributes a real (positive-factor) dim weight does the summed total
  // become meaningful. With no Dim config at all, itemDimWeightLb returns
  // null for every draft, this stays false, and we hand back dimWeightLb:null
  // so SummaryBar renders "—" rather than "0.00".
  let anyDimWeight = false;
  const add = (draft) => {
    pcs += Math.max(parseInt(draft.item_quantity, 10) || 0, 0);
    hus += Math.max(parseInt(draft.handling_units, 10) || 0, 0);
    volumeFt3 += itemVolumeFt3(draft);
    weightLb += itemWeightLb(draft);
    // Use THIS item's own selected Dim factor (draft.dim_factor) so a per-item
    // Dim dropdown change moves the summary panel's DIM WT live, falling back
    // to the shared account factor when the item has none — mirrors
    // computeLiveUnitTotals' per-item `itemFactor` and ItemCard's computedDimWt.
    // Without this, the no-units draft path summed every item at the single
    // shared `dimFactor`, so the panel total ignored the dropdown.
    const dim = itemDimWeightLb(draft, draft.dim_factor || dimFactor, dimWeightBasis);
    if (dim !== null && dim !== undefined) {
      anyDimWeight = true;
      dimWeightLb += dim;
    }
  };
  (items || []).forEach((item) => {
    const entry = itemDrafts[item.id];
    add(entry ? entry.draft : buildDraft(item));
  });
  (pendingNewItems || []).forEach((p) => {
    // Wave B review IMPORTANT-3: an untouched/empty "Add Item" card is
    // silently DROPPED by Save (handleSaveAll's isEmptyDraft filter), so
    // counting its default 1 pc / 1 HU here would overstate the live tiles
    // relative to what Save actually produces. Same predicate, so the tile
    // and the Save flow can never disagree about which cards are real.
    if (isEmptyDraft(p.draft)) return;
    add(p.draft);
  });
  return { pcs, hus, volumeFt3, weightLb, dimWeightLb: anyDimWeight ? dimWeightLb : null };
}

// Freight density = ACTUAL weight per unit volume:
//   PCF = weight (lb) / volume (ft³)
//   KCM = weight (kg) / volume (m³), where kg = lb × 0.453592
// Density is only meaningful with real volume. Below a small threshold
// (no dims / partially-entered dims → volume ≈ 0), weight ÷ ~0 explodes into
// absurd figures, so return null (caller renders "—") instead. 0.1 ft³ ≈ a
// 5-inch cube — anything smaller is treated as "no meaningful volume yet"
// (matches SummaryBar's prior `densityAvailable` threshold).
export function density(weightLb, volumeFt3) {
  const ft3 = parseFloat(volumeFt3) || 0;
  if (ft3 < 0.1) return { pcf: null, kcm: null };
  const weight = parseFloat(weightLb) || 0;
  const volumeM3 = ft3 * M3_PER_FT3;
  const pcf = weight / ft3;
  // lb → kg is a MULTIPLY by 0.453592 (byte-identical to SummaryBar's prior
  // `lbToKg(weight) / volumeM3`). Do NOT divide here.
  const kcm = (weight * LB_PER_KG) / volumeM3;
  return { pcf, kcm };
}
